import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Wifi, Coffee, Users, Car, Star, Building2, Waves, Home, BedDouble, UtensilsCrossed } from 'lucide-react';
import { useTrackPageView, trackEvent } from '@/hooks/useAnalytics';

const Hotels = () => {
  useTrackPageView({ path: '/', pageName: 'Home - Eye Hotels & Events' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    hotel: '',
    message: ''
  });
  const contactFormRef = useRef(null);
  const hotelsSectionRef = useRef(null);
  const meetingsSectionRef = useRef(null);

  const handleSelectHotel = (hotelName) => {
    setFormData((prev) => ({ ...prev, hotel: hotelName }));
    trackEvent({ eventType: 'hotel_click', hotel: hotelName, label: 'select-hotel-cta' });
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.hotel) {
      toast.error('Selecciona un hotel de interés antes de enviar.');
      return;
    }
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/contact-request`, formData);
      trackEvent({ eventType: 'form_submit', hotel: formData.hotel, label: 'contact-form' });
      toast.success('¡Gracias! Hemos enviado tu solicitud. Nos pondremos en contacto contigo pronto.');
      setFormData({ name: '', email: '', phone: '', hotel: '', message: '' });
    } catch (error) {
      console.error('Error sending contact request:', error);
      toast.error('Error al enviar la solicitud. Inténtalo de nuevo.');
    }
  };

  const whatsappNumber = "34658785334";
  const whatsappMessage = "Hola, me gustaría información sobre vuestros hoteles";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="relative" data-testid="hotels-page">
      {/* WhatsApp Floating Button - Fixed bottom right */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
        data-testid="whatsapp-btn"
        aria-label="Contactar por WhatsApp"
      >
        <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <div className="space-y-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div
          className="relative rounded-2xl overflow-hidden bg-center bg-cover"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1758193783649-13371d7fb8dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGxvYmJ5JTIwcmVjZXB0aW9uJTIwZGVzayUyMGludGVyaW9yfGVufDB8fHx8MTc3MDQwMTUwNnww&ixlib=rb-4.1.0&q=85')",
            backgroundPosition: 'center 75%'
          }}
        >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/45 to-purple-900/25"></div>
        <div className="relative px-8 pt-6 pb-12 md:pt-10 md:pb-20 text-center text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 mb-4 -mt-4 max-w-5xl mx-auto" data-testid="hotel-logos-row">
            <div className="flex justify-center md:justify-start" data-testid="logo-ciutat-martorell">
              <img
                src="https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/kwv2w37j_Sin%20t%C3%ADtulo-Photoroom.png"
                alt="Hotel Ciutat Martorell"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
            <div className="flex justify-center" data-testid="logo-port-sitges">
              <img
                src="https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/j0vqfwfl_logo%20port.png"
                alt="Hotel Port Sitges"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
            <div className="flex justify-center md:justify-end" data-testid="logo-san-francisco">
              <img
                src="https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/f9lau7t4_image-Photoroom.png"
                alt="Hostal San Francisco"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Hoteles para Eventos Corporativos en Barcelona y Extremadura
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
            3 establecimientos únicos para eventos, reuniones MICE, escapadas y turismo rural
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              type="button"
              onClick={() => scrollToSection(hotelsSectionRef)}
              className="inline-flex items-center bg-white text-blue-700 text-lg py-2 px-4 rounded-full shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid="hero-scroll-hotels"
            >
              <MapPin className="h-5 w-5 mr-2" />
              Hoteles
            </button>
            <button
              type="button"
              onClick={() => scrollToSection(meetingsSectionRef)}
              className="inline-flex items-center bg-white text-blue-700 text-lg py-2 px-4 rounded-full shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid="hero-scroll-meetings"
            >
              <Building2 className="h-5 w-5 mr-2" />
              Instalaciones
            </button>
            <button
              type="button"
              onClick={() => scrollToSection(contactFormRef)}
              className="inline-flex items-center bg-white text-blue-700 text-lg py-2 px-4 rounded-full shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              data-testid="hero-scroll-contact"
            >
              <Mail className="h-5 w-5 mr-2" />
              Contacto
            </button>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200/70" data-testid="divider-hero-hotels"></div>

      {/* Hotels Grid */}
      <div className="space-y-8" ref={hotelsSectionRef} data-testid="hotels-section">
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-3" data-testid="hotels-title-decor">
            <span className="h-px w-20 bg-gradient-to-r from-transparent to-gray-300" data-testid="hotels-title-decor-left"></span>
            <span className="h-2 w-2 rounded-full bg-blue-500/70" data-testid="hotels-title-decor-center"></span>
            <span className="h-px w-20 bg-gradient-to-l from-transparent to-gray-300" data-testid="hotels-title-decor-right"></span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Descubre Nuestros Hoteles en Barcelona y Extremadura</h2>
          <p className="mt-2 text-gray-600">Alojamiento con carácter propio, salas de reuniones y servicios para empresas, grupos y viajeros</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Hotel Ciutat Martorell */}
          <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow flex flex-col" data-testid="hotel-card-ciutat-martorell">
            <div className="relative h-48 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/rteyqita_image.png"
                alt="Hotel Ciutat Martorell - Hotel moderno cerca de SEAT Volkswagen en Martorell Barcelona"
                title="Hotel Ciutat Martorell - Alojamiento para viajes de negocios"
                loading="lazy"
                className="w-full h-full object-cover object-[50%_70%] hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute top-4 left-4 bg-blue-50 text-blue-700 px-3 py-1 rounded-full flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span className="font-semibold text-sm">Hotel Ciutat Martorell</span>
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-xl">Junto a SEAT/Volkswagen - Ideal para Industria</CardTitle>
              <CardDescription className="text-gray-600">
                Hotel moderno a solo 5 min del complejo SEAT. Ideal para visitas a SEAT, Volkswagen y zona industrial. Rápido acceso A-2/AP-7. A 20 min de Fira Barcelona.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Building2 className="h-4 w-4 text-blue-700" />
                  <span>5 min SEAT/VW</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Car className="h-4 w-4 text-blue-700" />
                  <span>Acceso A-2/AP-7</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Users className="h-4 w-4 text-blue-700" />
                  <span>Salas + Reuniones</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Coffee className="h-4 w-4 text-blue-700" />
                  <span>20' Fira BCN</span>
                </div>
              </div>
              <div className="flex gap-2 mt-auto pt-4">
                <Button 
                  className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => handleSelectHotel('Hotel Ciutat Martorell')}
                  data-testid="request-info-ciutat-martorell"
                >
                  Solicitar Info
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => window.open('https://www.hotel-martorell.es', '_blank')}
                  data-testid="visit-web-ciutat-martorell"
                >
                  Ver Web
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Port Sitges Hotel */}
          <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow flex flex-col" data-testid="hotel-card-port-sitges">
            <div className="relative h-48 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_que-servicio-1/artifacts/042rchko_09_%20SUNSET%2021%2004%2022_.%20Photo%20by%20%C2%A9%20Imanol%20Sistiagatif.jpg"
                alt="Port Sitges Hotel - Hotel con vistas al mar Mediterráneo en Sitges Barcelona"
                title="Port Sitges Hotel - Hotel frente al mar con terraza y piscina"
                loading="lazy"
                className="w-full h-full object-cover object-[50%_75%] hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute top-4 left-4 bg-cyan-50 text-cyan-700 px-3 py-1 rounded-full flex items-center gap-2">
                <Waves className="h-4 w-4" />
                <span className="font-semibold text-sm">Port Sitges Hotel</span>
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-xl">Vistas al Mar - Mejor opción para Grupos y Eventos</CardTitle>
              <CardDescription className="text-gray-600">
                A 30' de Barcelona. Vistas al mar, terraza y piscina. Salas de reuniones con luz natural. Ideal para grupos y eventos cerca del mar con ambiente relajante.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Waves className="h-4 w-4 text-cyan-700" />
                  <span>Vistas al mar</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Coffee className="h-4 w-4 text-cyan-700" />
                  <span>Sala luz natural</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Users className="h-4 w-4 text-cyan-700" />
                  <span>Grupos & eventos</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Wifi className="h-4 w-4 text-cyan-700" />
                  <span>Terraza + Piscina</span>
                </div>
              </div>
              <div className="flex gap-2 mt-auto pt-4">
                <Button 
                  className="flex-1 h-11 bg-cyan-600 hover:bg-cyan-700 text-white"
                  onClick={() => handleSelectHotel('Port Sitges Hotel')}
                  data-testid="request-info-port-sitges"
                >
                  Solicitar Info
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => window.open('https://www.portsitgeshotel.com', '_blank')}
                  data-testid="visit-web-port-sitges"
                >
                  Ver Web
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Hostal San Francisco */}
          <Card className="h-full overflow-hidden hover:shadow-xl transition-shadow flex flex-col" data-testid="hotel-card-san-francisco">
            <div className="relative h-48 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_que-servicio-1/artifacts/6qehu0iw_ChatGPT%20Image%206%20feb%202026%2C%2003_43_26%20p.m..png"
                alt="Hostal San Francisco - Granja de Torrehermosa Badajoz - Turismo rural Extremadura"
                title="Hostal San Francisco - Alojamiento rural en Granja de Torrehermosa"
                loading="lazy"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
              />
              <div className="absolute top-4 left-4 bg-orange-50 text-orange-700 px-3 py-1 rounded-full flex items-center gap-2">
                <Home className="h-4 w-4" />
                <span className="font-semibold text-sm">Hostal San Francisco</span>
              </div>
            </div>
            <CardHeader>
              <CardTitle className="text-xl">Granja de Torrehermosa, Badajoz</CardTitle>
              <CardDescription className="text-gray-600">
                Hostal con encanto en plena Extremadura. Ideal para turismo rural, grupos pequeños y escapadas tranquilas. Excelente relación calidad-precio en un entorno natural único.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MapPin className="h-4 w-4 text-orange-700" />
                  <span>Extremadura</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Coffee className="h-4 w-4 text-orange-700" />
                  <span>Restaurante</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Wifi className="h-4 w-4 text-orange-700" />
                  <span>WiFi gratuito</span>
                </div>
              </div>
              <div className="flex gap-2 mt-auto pt-4">
                <Button 
                  className="flex-1 h-11 bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => handleSelectHotel('Hostal San Francisco')}
                  data-testid="request-info-san-francisco"
                >
                  Solicitar Info
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => window.open('https://www.granjadetorrehermosahostal.com', '_blank')}
                  data-testid="visit-web-san-francisco"
                >
                  Ver Web
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Meeting Rooms Section */}
      <div className="h-px bg-gray-200/70" data-testid="divider-hotels-location"></div>

      <div
        className="relative overflow-hidden rounded-2xl bg-center bg-cover"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/a0taxz7u_portdesitges04.jpg')"
        }}
        data-testid="feature-location-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/55"></div>
        <div className="relative p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4" data-testid="feature-location-title">
            Ubicaciones Privilegiadas en Sitges y Barcelona
          </h2>
          <p className="text-white/90 mb-6" data-testid="feature-location-description">
            Hotel frente al mar en Sitges y hotel de negocios junto al polo industrial de Barcelona. Conexiones directas por autopista y transporte público.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Frente al mar</h3>
                <p className="text-sm text-white/80">Port Sitges Hotel con ambiente náutico y vistas al Mediterráneo.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Conexión Barcelona</h3>
                <p className="text-sm text-white/80">Hotel Ciutat Martorell con acceso rápido a la ciudad y al entorno industrial.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200/70" data-testid="divider-location-meetings"></div>

      <div
        className="relative overflow-hidden rounded-2xl bg-center bg-cover"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/dx6objie_Garb%C3%AD_3.jpg')"
        }}
        ref={meetingsSectionRef}
        data-testid="meetings-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
        <div className="relative p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Salas de Reuniones y Eventos MICE en Barcelona
          </h2>
          <p className="text-white/90 mb-6">
            Espacios profesionales equipados para reuniones de empresa, conferencias, incentivos y convenciones. Servicio integral con catering y soporte técnico.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Capacidad Flexible</h3>
                <p className="text-sm text-white/80">De 10 a 200 personas según tus necesidades</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <Wifi className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Tecnología Moderna</h3>
                <p className="text-sm text-white/80">Proyectores, pantallas, WiFi de alta velocidad</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <Coffee className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Servicios de Catering</h3>
                <p className="text-sm text-white/80">Coffee breaks, comidas y menús personalizados</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200/70" data-testid="divider-meetings-catering"></div>

      <div
        className="relative overflow-hidden rounded-2xl bg-center bg-cover"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/wnoack6d_image.png')"
        }}
        data-testid="feature-catering-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/55"></div>
        <div className="relative p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4" data-testid="feature-catering-title">
            Catering de alta calidad
          </h2>
          <p className="text-white/90 mb-6" data-testid="feature-catering-description">
            Servicio impecable para coffee breaks, almuerzos ejecutivos y eventos con propuestas gastronómicas cuidadas.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <UtensilsCrossed className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Presentación premium</h3>
                <p className="text-sm text-white/80">Detalles gourmet y un servicio atento en cada evento.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <UtensilsCrossed className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Calidad y sabor</h3>
                <p className="text-sm text-white/80">Ingredientes frescos con opciones adaptadas a tus necesidades.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200/70" data-testid="divider-catering-rooms"></div>

      <div
        className="relative overflow-hidden rounded-2xl bg-center bg-cover"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_hotelcrm-pro/artifacts/e3a30kjk_Ciutat_Martorell-Martorell-Single_room_standard-1-169610.jpg')",
          backgroundPosition: 'center 70%'
        }}
        data-testid="feature-rooms-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/55"></div>
        <div className="relative p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4" data-testid="feature-rooms-title">
            Habitaciones modernas y cómodas
          </h2>
          <p className="text-white/90 mb-6" data-testid="feature-rooms-description">
            Espacios luminosos y funcionales, diseñados para un descanso total tras tu jornada de trabajo o de viaje.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <BedDouble className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Confort contemporáneo</h3>
                <p className="text-sm text-white/80">Camas amplias, materiales cálidos y diseño elegante.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-white/15 p-2 rounded-lg">
                <BedDouble className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Descanso garantizado</h3>
                <p className="text-sm text-white/80">Ambiente tranquilo y amenities pensadas para tu comodidad.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200/70" data-testid="divider-rooms-contact"></div>

      {/* Contact Form */}
      <Card ref={contactFormRef} data-testid="contact-form-section" id="contacto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Solicita Información - Reservas y Eventos</CardTitle>
          <CardDescription>
            Cuéntanos tu evento o reserva y te enviaremos una propuesta personalizada sin compromiso
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name">Nombre *</Label>
                <Input
                  id="contact-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-email">Email *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-phone">Teléfono</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-hotel">Hotel de Interés</Label>
                <Select value={formData.hotel} onValueChange={(value) => setFormData({ ...formData, hotel: value })}>
                  <SelectTrigger id="contact-hotel" data-testid="contact-hotel-select">
                    <SelectValue placeholder="Selecciona un hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hotel Ciutat Martorell" data-testid="contact-hotel-ciutat-martorell">
                      Hotel Ciutat Martorell
                    </SelectItem>
                    <SelectItem value="Port Sitges Hotel" data-testid="contact-hotel-port-sitges">
                      Port Sitges Hotel
                    </SelectItem>
                    <SelectItem value="Hostal San Francisco" data-testid="contact-hotel-san-francisco">
                      Hostal San Francisco
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="contact-message">Mensaje *</Label>
              <Textarea
                id="contact-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                placeholder="Cuéntanos sobre tu evento o reunión..."
                required
                data-testid="contact-message-input"
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white" data-testid="submit-contact-form">
              <Mail className="h-4 w-4 mr-2" />
              Enviar Solicitud
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="h-px bg-gray-200/70" data-testid="divider-contact-footer"></div>

      {/* Footer Info */}
      <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <CardContent className="py-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Building2 className="h-12 w-12 mx-auto mb-3 text-blue-400" />
              <h3 className="font-semibold text-lg mb-2">Hotel Ciutat Martorell</h3>
              <p className="text-gray-300 text-sm">Martorell, Barcelona</p>
              <p className="text-gray-300 text-sm font-semibold mt-1">5 min SEAT/Volkswagen</p>
              <p className="text-gray-400 text-xs mt-2">info@hotel-martorell.com</p>
            </div>
            <div>
              <Waves className="h-12 w-12 mx-auto mb-3 text-cyan-400" />
              <h3 className="font-semibold text-lg mb-2">Port Sitges Hotel</h3>
              <p className="text-gray-300 text-sm">Puerto de Aiguadolç, Sitges</p>
              <p className="text-gray-300 text-sm font-semibold mt-1">30' Barcelona - Vistas al Mar</p>
              <p className="text-gray-400 text-xs mt-2">info@portsitgeshotel.com</p>
            </div>
            <div>
              <Home className="h-12 w-12 mx-auto mb-3 text-orange-400" />
              <h3 className="font-semibold text-lg mb-2">Hostal San Francisco</h3>
              <p className="text-gray-300 text-sm">Granja de Torrehermosa</p>
              <p className="text-gray-300 text-sm">Badajoz, Extremadura</p>
              <p className="text-gray-400 text-xs mt-2">reservas@granjadetorrehermosahostal.com</p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Hotels;
